import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import type {
  AssistantInsightsFilters,
  InsightsWorkspace,
} from '@/api/assistantInsights/assistantInsightsApi.types';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useAssistantInsightsWorkspacesQuery } from '@/hooks/useAssistantInsights';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { formatCompactNumber, formatMicroUsd, formatPercent } from './usageFormatting';

const BUDGET_WARNING_RATIO = 0.8;

interface UsageWorkspaceTableProps {
  filters: AssistantInsightsFilters;
  selectedTenantId: string | null;
  onSelect: (workspace: InsightsWorkspace | null) => void;
}

function BudgetBar({ ratio, language }: { ratio: number; language: string }) {
  const clamped = Math.min(1, ratio);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 rounded-full bg-zoovpalelavender overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full',
            ratio >= BUDGET_WARNING_RATIO ? 'bg-zoowarningtext' : 'bg-zooprimary'
          )}
          style={{ width: String(Math.round(clamped * 100)) + '%' }}
        />
      </div>
      <span className="text-xs text-zoomuted w-10 text-right">
        {formatPercent(ratio, language)}
      </span>
    </div>
  );
}

export function UsageWorkspaceTable({
  filters,
  selectedTenantId,
  onSelect,
}: UsageWorkspaceTableProps) {
  const { t, i18n } = useTranslation('assistant');
  const language = i18n.language;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MILLISECONDS);
  const workspacesQuery = useAssistantInsightsWorkspacesQuery(
    { ...filters, tenantId: null },
    page,
    debouncedSearch
  );
  const filterKey = JSON.stringify({ ...filters, tenantId: null });

  useEffect(
    function returnToFirstPage() {
      setPage(1);
    },
    [debouncedSearch, filterKey]
  );

  const workspaces = workspacesQuery.data === undefined ? [] : workspacesQuery.data.items;
  const totalItems = workspacesQuery.data === undefined ? 0 : workspacesQuery.data.totalItems;

  return (
    <Card as="section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-bold text-zooindigo">{t('insights.workspaces.title')}</h2>
          <p className="text-xs text-zoomuted mt-0.5">{t('insights.workspaces.hint')}</p>
        </div>
        <ListSearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('insights.workspaces.search')}
        />
      </div>
      {workspacesQuery.isLoading ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : workspacesQuery.isError ? (
        <QueryErrorState
          onRetry={function retry() {
            void workspacesQuery.refetch();
          }}
        />
      ) : workspaces.length === 0 ? (
        <p className="py-8 text-center text-sm text-zoomuted">
          {debouncedSearch.trim().length > 0
            ? t('insights.workspaces.noMatch')
            : t('insights.workspaces.empty')}
        </p>
      ) : (
        <div className="relative overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zoomuted border-b border-zooborder">
                <th className="py-2 pr-4 font-semibold">
                  {t('insights.workspaces.columns.workspace')}
                </th>
                <th className="py-2 pr-4 font-semibold text-right">
                  {t('insights.workspaces.columns.cost')}
                </th>
                <th className="py-2 pr-4 font-semibold text-right">
                  {t('insights.workspaces.columns.questions')}
                </th>
                <th className="py-2 pr-4 font-semibold text-right">
                  {t('insights.workspaces.columns.perQuestion')}
                </th>
                <th className="py-2 pr-4 font-semibold text-right">
                  {t('insights.workspaces.columns.users')}
                </th>
                <th className="py-2 pr-4 font-semibold text-right">
                  {t('insights.workspaces.columns.cache')}
                </th>
                <th className="py-2 pr-4 font-semibold">
                  {t('insights.workspaces.columns.budget')}
                </th>
                <th className="py-2 font-semibold">
                  <span className="sr-only">{t('insights.workspaces.columns.open')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map(function renderRow(workspace) {
                const isSelected = workspace.tenantId === selectedTenantId;
                return (
                  <tr
                    key={workspace.tenantId}
                    className={cn(
                      'border-b border-zooborder last:border-b-0 cursor-pointer transition-colors',
                      isSelected ? 'bg-zoovpalelavender' : 'hover:bg-zoooffwhite'
                    )}
                    onClick={function focusWorkspace() {
                      onSelect(isSelected ? null : workspace);
                    }}
                  >
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        className="font-semibold text-zooindigo text-left hover:text-zooprimary"
                        onClick={function focusFromButton(event) {
                          event.stopPropagation();
                          onSelect(isSelected ? null : workspace);
                        }}
                      >
                        {workspace.tenantName}
                      </button>
                    </td>
                    <td className="py-3 pr-4 text-right font-semibold text-zooindigo">
                      {formatMicroUsd(workspace.costMicroUsd, language)}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      {formatCompactNumber(workspace.questions, language)}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      {formatMicroUsd(workspace.costPerQuestionMicroUsd, language)}
                    </td>
                    <td className="py-3 pr-4 text-right">{workspace.activeUsers}</td>
                    <td className="py-3 pr-4 text-right">
                      {formatPercent(workspace.cacheRate, language)}
                    </td>
                    <td className="py-3 pr-4">
                      <BudgetBar ratio={workspace.budgetUsedThisMonth} language={language} />
                    </td>
                    <td className="py-3 text-right">
                      <Link
                        to={generatePath(APPLICATION_ROUTES.adminTenantDetail, {
                          tenantId: workspace.tenantId,
                        })}
                        onClick={function keepRowSelection(event) {
                          event.stopPropagation();
                        }}
                        aria-label={t('insights.workspaces.open', { name: workspace.tenantName })}
                        title={t('insights.workspaces.open', { name: workspace.tenantName })}
                        className="inline-flex w-8 h-8 items-center justify-center rounded-lg text-zoomuted hover:bg-zoovpalelavender hover:text-zooprimary"
                      >
                        <Icon name="ExternalLink" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4">
        <ListPagination
          page={page}
          pageSize={ADMIN_LIST_PAGE_SIZE}
          totalItems={totalItems}
          onPageChange={setPage}
        />
      </div>
    </Card>
  );
}
